/**
 * Circuit Breaker pattern implementation for API resilience.
 * Prevents cascading failures by temporarily blocking requests to failing endpoints.
 */

export interface CircuitBreakerConfig {
  /** Maximum number of failures before opening the circuit (default: 3) */
  maxFailures?: number;
  /** Time in ms before attempting to reset after failures (default: 60000 = 1 minute) */
  resetTimeout?: number;
}

export class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private readonly maxFailures: number;
  private readonly resetTimeout: number;

  constructor(config: CircuitBreakerConfig = {}) {
    this.maxFailures = config.maxFailures ?? 3;
    this.resetTimeout = config.resetTimeout ?? 60000; // 1 minute
  }

  /**
   * Check if a request can be made (circuit is closed or half-open)
   */
  canMakeRequest(): boolean {
    if (this.failureCount < this.maxFailures) {
      return true;
    }

    // Check if enough time has passed to reset (half-open state)
    if (Date.now() - this.lastFailureTime > this.resetTimeout) {
      this.reset();
      return true;
    }

    return false;
  }

  /**
   * Record a failed request
   */
  recordFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();
  }

  /**
   * Record a successful request (resets the circuit)
   */
  recordSuccess(): void {
    this.reset();
  }

  /**
   * Get current circuit state for debugging
   */
  getState(): { failureCount: number; isOpen: boolean; lastFailureTime: number } {
    return {
      failureCount: this.failureCount,
      isOpen: this.failureCount >= this.maxFailures,
      lastFailureTime: this.lastFailureTime,
    };
  }

  private reset(): void {
    this.failureCount = 0;
    this.lastFailureTime = 0;
  }
}

/**
 * Create a circuit breaker with default configuration
 */
export function createCircuitBreaker(config?: CircuitBreakerConfig): CircuitBreaker {
  return new CircuitBreaker(config);
}
